import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import type { LayoutChangeEvent } from "@-/com.npmjs.react-native";
import { View } from "@-/com.npmjs.react-native";
import { type ComponentProps, useCallback, useEffect, useRef, useState } from "react";

/**
 * Fraction of `fontSize` used as the line box when no explicit `lineHeight` is given. Matches
 * the `leading-normal` (1.5×) line height `Text` applies to every non-`glyph`/`small`/`p`
 * variant, so the fitting math predicts the same box the platform actually lays out.
 */
const DEFAULT_LINE_HEIGHT_RATIO = 1.5;

/**
 * Tolerance (in px) when comparing a measured height against the allowance. Sub-pixel layout
 * rounding otherwise reports a perfectly fitting line as one pixel too tall and triggers an
 * unnecessary shrink step.
 */
const FIT_EPSILON = 1;

/**
 * Extra shrink applied on top of the measured overflow ratio. Text does not scale perfectly
 * linearly with font size (a smaller font can pull a word onto the previous line, or fail to),
 * so the ratio alone can land a hair too large and cost another pass; this nudges past that.
 */
const SHRINK_OVERSHOOT = 0.02;

type AutoSizeTextProps = Omit<
  ComponentProps<typeof Text>,
  "adjustsFontSizeToFit" | "minimumFontScale" | "numberOfLines" | "onLayout"
> & {
  /**
   * Font size, in px, the text starts at. It is only reduced when the content cannot fit in
   * `maxLines` at this size.
   */
  fontSize: number;
  /** Explicit line box in px. Defaults to `fontSize * 1.5`, matching `Text`'s `leading-normal`. */
  lineHeight?: number;
  /** Lines the text may wrap onto before the font starts shrinking. */
  maxLines?: number;
  /**
   * Lower bound on the shrink, as a fraction of `fontSize`. Content that still overflows at
   * this scale wraps past `maxLines` rather than being truncated — the text is never clipped
   * or ellipsized.
   */
  minimumFontScale?: number;
};

/**
 * Text that wraps onto multiple lines and, only once wrapping alone is not enough, shrinks its
 * font until the content fits `maxLines` — never truncating with an ellipsis.
 *
 * Unlike React Native's `adjustsFontSizeToFit`, this works identically on iOS, Android, and
 * web: it measures the laid-out text and steps the font size down until it fits, so no platform
 * is left with an unshrunk (web) or unreliably shrunk (Android) title.
 *
 * The measurement runs against the real rendered node rather than a hidden probe, so the first
 * paint is at `fontSize` and settles within a couple of layout passes. Passing no `numberOfLines`
 * to the underlying `Text` is deliberate: at `minimumFontScale` an unfittable string overflows
 * onto extra lines instead of being cut off.
 */
export function AutoSizeText({
  children,
  className,
  fontSize,
  lineHeight,
  maxLines = 2,
  minimumFontScale = 0.5,
  style,
  ...props
}: AutoSizeTextProps) {
  const [scale, setScale] = useState(1);
  // Width and content both change what fits, so a new value of either has to restart the
  // shrink from 1 rather than keep a scale that was fitted to the previous layout.
  const widthRef = useRef<number | null>(null);

  useEffect(() => {
    setScale(1);
  }, [children, fontSize, lineHeight, maxLines, minimumFontScale]);

  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    if (widthRef.current !== null && Math.abs(widthRef.current - width) < FIT_EPSILON) {
      return;
    }
    widthRef.current = width;
    setScale(1);
  }, []);

  const handleTextLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { height } = event.nativeEvent.layout;
      const resolvedLineHeight = lineHeight ?? fontSize * DEFAULT_LINE_HEIGHT_RATIO;
      const allowance = resolvedLineHeight * scale * maxLines + FIT_EPSILON;

      if (height <= allowance || scale <= minimumFontScale) {
        return;
      }

      // Scale by how much the text overflowed rather than by a fixed step, so a badly
      // oversized title converges in one or two layout passes instead of a visible staircase
      // of shrinking repaints.
      const nextScale = Math.max(minimumFontScale, scale * (allowance / height) - SHRINK_OVERSHOOT);
      // Guard against a non-shrinking update: an unchanged scale would re-fire this same
      // layout callback forever.
      if (nextScale >= scale) {
        return;
      }
      setScale(nextScale);
    },
    [fontSize, lineHeight, maxLines, minimumFontScale, scale],
  );

  const resolvedFontSize = fontSize * scale;
  const resolvedLineHeight = (lineHeight ?? fontSize * DEFAULT_LINE_HEIGHT_RATIO) * scale;

  return (
    <View className="w-full min-w-0" onLayout={handleContainerLayout}>
      <Text
        {...props}
        className={cn("w-full", className)}
        onLayout={handleTextLayout}
        style={[{ fontSize: resolvedFontSize, lineHeight: resolvedLineHeight }, style]}
      >
        {children}
      </Text>
    </View>
  );
}
